<!--CartModal.vue-->
<template>
  <div class="cart-modal-backdrop" @click.self="emitCloseModal">
    
    <div class="cart-modal">
      <!-- 關閉按鈕 -->
      <button class="close-btn" @click="emitCloseModal">×</button>

      <!-- 如果商品資料還在 Loading -->
      <div v-if="!productDetailsLoaded">
        <p>Loading...</p>
      </div>

      <!-- 詳細內容 (當資料載入完成後) -->
      <div v-else class="detail-top-section">

        <!-- 左側媒體區 -->
        <div class="detail-media">
          <div class="media-viewer">
            <!-- 主視區 -->
            <div class="main-display">
              <!-- 左箭頭 -->
              <button v-if="hasPrev" class="arrow arrow-left" @click="prevMedia">
                ‹
              </button>

              <!-- Facebook 影片 -->
              <template v-if="currentMedia.media_type === 'facebook'">
                <div class="fb-video" :data-href="currentMedia.media_url" data-width="500" data-show-text="false"></div>
              </template>

              <!-- YouTube or mp4 -->
              <template v-else-if="currentMedia.media_type === 'youtube'">
                <!-- YouTube -->
                <iframe v-if="currentMedia.youtube_id" class="responsive-iframe"
                  :src="`https://www.youtube.com/embed/${currentMedia.youtube_id}`" frameborder="0"
                  allowfullscreen></iframe>
                <!-- mp4 -->
                <video v-else controls class="responsive-video" :src="currentMedia.media_url"></video>
              </template>

              <!-- 圖片 + 放大鏡 -->
              <template v-else-if="currentMedia.media_type === 'image'">
                <div class="magnifier-container" @mousemove="moveLens" @mouseleave="hideLens" ref="magnifierWrapper">
                  <img class="main-image" :src="currentMedia.media_url" ref="mainImgRef" />
                  <!-- 放大鏡鏡頭 (Lens) -->
                  <div class="magnifier-lens" v-if="lensVisible" :style="lensStyle"></div>

                  <!-- 放大後顯示區 -->
                  <div class="magnifier-result" v-if="lensVisible" :style="resultStyle"></div>
                </div>
              </template>

              <!-- 沒有媒體 -->
              <div v-else class="no-media">
                找不到媒體
              </div>

              <!-- 右箭頭 -->
              <button v-if="hasNext" class="arrow arrow-right" @click="nextMedia">
                ›
              </button>
            </div>

            <!-- 縮略圖列表 (pMedias) -->
            <div class="thumbnail-list">
              <div class="thumbnail" v-for="(media, i) in productDetails.pMedias" :key="i" @click="currentIndex = i">
                <!-- 如果是影片 (YouTube) -->
                <img v-if="media.media_type === 'youtube' && media.youtube_id"
                  :src="getYoutubeThumbnail(media.youtube_id)" alt="YouTubeThumb" />
                <!-- Facebook 影片暫用圖 -->
                <img v-else-if="media.media_type === 'facebook'" src="/img/fb-placeholder.jpg" alt="FBThumb" />
                <!-- mp4 或圖片 -->
                <img v-else :src="media.media_url" alt="Thumb" />
              </div>
            </div>
          </div>
        </div>


        <!-- 右側：商品標題, 規格選擇, 價格, +購物車 -->
        <div class="detail-info">
          <!-- 僅供除錯觀看：{{ productDetails }} -->

          <h2>{{ productDetails.title }}</h2>
          <p class="product-desc">{{ productDetails.description }}</p>

          <!-- 運送 / 付款方式區 -->
          <div class="shipping-info">
            <h4>運送 / 付款方式</h4>
            <ul>
              <li v-for="method in shippingMethods" :key="method.id">
                <img v-if="method.icon_url" :src="method.icon_url" :alt="method.name" />
                {{ method.name }}，運費 NT$ {{ method.shipping_cost }}，
                滿 NT$ {{ method.free_shipping_threshold }} 免運
              </li>
            </ul>
          </div>

          <!-- 價格顯示 -->
          <div class="price">
            <!-- (1) 若有選規格 → 顯示該規格原價 / 折扣價 -->
            <template v-if="selectedVariant">
              <del v-if="Number(selectedVariant.original_price) !== Number(selectedVariant.discount_price)">
                NT${{ selectedVariant.original_price }}
              </del>
              <span class="discount-price">
                NT${{ selectedVariant.discount_price }}
              </span>
            </template>

            <!-- (2) 無選規格 → 顯示「最低 ~ 最高」 -->
            <template v-else>
              <del v-if="productDetails.price !== productDetails.discount_price">
                NT${{ productDetails.price }} ~ {{ productDetails.highest_price }}
              </del>
              <span class="discount-price">
                NT${{ productDetails.discount_price }} ~ {{ productDetails.highest_discount }}
              </span>
            </template>
          </div>

          <div class="stock-sold-wrapper" v-if="productDetails.sold_quantity > 0">
            <div class="info-box sold-box">
              <i class="fas fa-fire-alt"></i>
              <span class="label">已售出&ensp;</span>
              <span class="number">{{ productDetails.sold_quantity }}</span>
              <span class="label">&ensp;{{ productDetails.unit }}</span>
            </div>

          </div>


          <!-- 規格選擇區 (若 productDetails.Variants 有多筆) -->
          <div class="variant-choices" v-if="productDetails.Variants && productDetails.Variants.length > 1">
            <p>請選擇規格：</p>
            <div class="variant-item" v-for="(variant, vIdx) in productDetails.Variants" :key="vIdx"
              @click="selectVariant(variant)" :class="{ 'is-active': selectedVariant === variant }">
              {{ variant.variant_name }}
            </div>
          </div>


          <!-- 數量控制區 -->
          <div class="qty-section">
            <div class="quantity-control">
              <button @click="decreaseQty" :disabled="localQuantity <= 1">-</button>
              <input inputmode="numeric" type="number" v-model.number="localQuantity" min="1" max="99"
                @blur="checkQuantity" />
              <button @click="increaseQty" :disabled="localQuantity >= 99">+</button>
            </div>
            <span class="qty-hint">(最多可選 99 件)</span>
          </div>

          <p v-if="errorMsg" class="error-text">{{ errorMsg }}</p>
          <!-- 加入購物車 / 取消 -->
          <div class="action-buttons">
            <button  ref="cartButton" class="confirm-add-cart" @click="emitAddToCart" :disabled="productDetails.stock === 0">
              {{productDetails.stock === 0 ? ' (已售完)' : '加入購物車'}}
            </button>
            <button class="cancel-button" @click="emitCloseModal">
              取消
            </button>
          </div>
        </div>
      </div>

      <!-- 若需要下方詳情，可自行打開 -->
      <!--
      <div class="detail-bottom-section">
        ...
      </div>
      -->
    </div>
  </div>
</template>

<script>
import { useSysTempStore } from '@/store/sysTempStore'  // 假設有 shippingStore
import { mapState, mapGetters, mapActions } from 'vuex'
import { getMediaType, getYoutubeId } from '@/utils/mediaUtil.js' // 你自己的工具函式
import { useToastStore } from '@/store/toastStore.js'
import { useCartStore } from '@/store/cartStore' // 若使用 Pinia
import axios from 'axios'

export default {
  name: 'CartModal',
  props: {
    isMobile: {
      type: Boolean,
      default: false
    },
    product: {
      type: Object,
      required: true
    },
  },
  data() {
    return {
      showFbVideo: false,
      shippingMethods: [],
      productDetails: null,
      productDetailsLoaded: false,
      currentIndex: 0,  // pMedias 當前顯示的索引
      lensVisible: false,
      lensStyle: {},
      resultStyle: {},
      localQuantity: 1,
      errorMsg: '',   // 新增，預設空字串
      // 用來記錄「目前選中」的規格。如果 = null，代表尚未選擇
      selectedVariant: null
    }
  },
  setup() {
    const toastStore = useToastStore()
    const cartStore = useCartStore()

    // 這裡把 toastStore return，才能在後續的 this.toastStore 中使用
    return {
      toastStore,
      cartStore,
    }
  },
  computed: {
    ...mapState(['cartItems']),
    ...mapGetters(['cartTotal', 'cartCount']),

    // 取得當前要顯示的媒體 (圖片 or 影片)
    currentMedia() {
      if (!this.productDetails || !this.productDetails.pMedias) return {}
      return this.productDetails.pMedias[this.currentIndex] || {}
    },
    // 是否可以往前
    hasPrev() {
      return this.currentIndex > 0
    },
    // 是否可以往後
    hasNext() {
      return (
        this.productDetails &&
        this.productDetails.pMedias &&
        this.currentIndex < this.productDetails.pMedias.length - 1
      )
    },
  },
  created: async function () {
    // 假設有 shippingStore 用來載入運送方式
    const useSysTemp = useSysTempStore()
    await useSysTemp.fetchShippingData()
    this.shippingMethods = useSysTemp.shippingMethods
  },
  mounted() {
    // FB 影片解析
    this.$nextTick(() => {
      if (window.FB && window.FB.XFBML) {
        window.FB.XFBML.parse()
      }
    })
    // 彈窗掛載後，再去撈商品詳情
    this.fetchProductDetails()
  },
  methods: {
    // 若使用 Vuex
    ...mapActions([
      'addProductToCart',   // from store
      'removeProductFromCart',
      'clearAllCart'
    ]),

    // 上一張媒體
    prevMedia() {
      if (this.hasPrev) {
        this.currentIndex--
      }
    },
    // 下一張媒體
    nextMedia() {
      if (this.hasNext) {
        this.currentIndex++
      }
    },

    // 撈取商品詳情 (例如 pMedias, Variants, etc.)
    fetchProductDetails() {
      axios
        .post('/api/load/productDetails', {
          product_id: this.product.product_id
        })
        .then(response => {
          if (response.data.success) {
            const newMedias = response.data.pMedias || []
            // 若有圖檔，就先判斷媒體類型
            newMedias.forEach(media => {
              media.media_type = getMediaType(media.media_url)
              media.youtube_id = getYoutubeId(media.media_url)
            })

            // 如果後端已經把全部參數都回傳了 (price, discount_price, Variants, ...)，可在此合併
            // 注意：response.data 只是一個範例
            this.productDetails = {
              ...this.product,
              ...response.data,      // 直接展開後端回傳的欄位
              pMedias: newMedias
            }

            // 如果只有1個 variant → 自動選取
            if (
              this.productDetails.Variants &&
              this.productDetails.Variants.length === 1
            ) {
              this.selectedVariant = this.productDetails.Variants[0]
            }

            this.productDetailsLoaded = true
          } else {
            console.error(response.data.message)
          }
        })
        .catch(err => {
          console.error(err)
        })
    },

    // 關閉 Modal
    emitCloseModal() {
      this.$emit('close-modal')
    },

    // 加入購物車
    emitAddToCart() {
      // 1. 若尚未選規格(且 Variants > 1)，可提醒
      //    （若該商品根本就只有1個規格，已自動選取）
      if (
        this.productDetails.Variants &&
        this.productDetails.Variants.length > 1 &&
        !this.selectedVariant
      ) {
        // this.toastStore.addToast(`請先選擇規格！`, 'warning');
        this.errorMsg = '請先選擇規格！';
        return
      }

      // 2. 組裝要放進購物車的資料
      // 注意：可以依照你實際後端 / 前端需求來決定裝哪些欄位
      // selectedVariant 若不存在，就用默認 productDetails (無 variant)  
      // → 不過通常多規格商品一定要有 Variant
      let currentVariant = this.selectedVariant || {}
      const payload = {
        description: this.productDetails.description,
        image_url: this.productDetails.image_url,
        title: this.productDetails.title,
        product_id: this.productDetails.product_id,
        variant_id: currentVariant.variant_id || 0, // or '' 
        variant_name: currentVariant.variant_name || '',
        discount_price: currentVariant.discount_price || this.productDetails.discount_price,
        quantity: this.localQuantity,
      }

      this.addProductToCart({
        product: payload,
        quantity: this.localQuantity
      })
      this.errorMsg = ''
      //console.log('加入購物車：', payload)
      this.toastStore.addToast(`成功加入購物車：${this.productDetails.title}`, 'success')
     // this.toastStore.addToast('成功加入購物車！', 'success', null)
      this.emitCloseModal();
      this.cartStore.toggleCartDropdown(true)
      setTimeout(() => {
        this.cartStore.toggleCartDropdown(false)
      }, 2000)

    },

    // 選擇某個規格
    selectVariant(variant) {
      this.selectedVariant = variant
    },

    // =======================
    // 放大鏡相關 methods
    // =======================
    moveLens(e) {
      if (this.isMobile || this.currentMedia.media_type !== 'image') return

      this.lensVisible = true

      let x = e.offsetX
      let y = e.offsetY

      const lensSize = 60
      if (x < lensSize) x = lensSize
      if (y < lensSize) y = lensSize

      const imgEl = this.$refs.mainImgRef
      const rect = imgEl.getBoundingClientRect()

      if (x > rect.width - lensSize) x = rect.width - lensSize
      if (y > rect.height - lensSize) y = rect.height - lensSize

      this.lensStyle = {
        display: 'block',
        top: (y - lensSize) + 'px',
        left: (x - lensSize) + 'px',
        width: (lensSize * 2) + 'px',
        height: (lensSize * 2) + 'px'
      }

      // 放大倍率
      const cx = 2
      const cy = 2

      const backgroundImage = `url(${this.currentMedia.media_url})`
      const backgroundSize = `${rect.width * cx}px ${rect.height * cy}px`
      const backgroundPosX = -(x * cx - lensSize) + 'px'
      const backgroundPosY = -(y * cy - lensSize) + 'px'

      const containerRect = this.$refs.magnifierWrapper.getBoundingClientRect()
      const resultWidth = 200
      let resultLeft = 'calc(100% + 10px)'
      if (containerRect.right + 10 + resultWidth > window.innerWidth) {
        resultLeft = `-${resultWidth + 10}px`
      }

      this.resultStyle = {
        left: resultLeft,
        backgroundImage,
        backgroundRepeat: 'no-repeat',
        backgroundSize,
        backgroundPosition: `${backgroundPosX} ${backgroundPosY}`
      }
    },
    hideLens() {
      this.lensVisible = false
      if (this.lensStyle) this.lensStyle.display = 'none'
    },

    // 取得 YouTube 縮圖
    getYoutubeThumbnail(youtubeId) {
      return `https://img.youtube.com/vi/${youtubeId}/0.jpg`
    },

    // =======================
    // 數量控制
    // =======================
    decreaseQty() {
      if (this.localQuantity > 1) this.localQuantity--
    },
    increaseQty() {
      if (this.localQuantity < 99) this.localQuantity++
    },
    checkQuantity() {
      if (!this.localQuantity || this.localQuantity < 1) {
        this.localQuantity = 1
      } else if (this.localQuantity > 99) {
        this.localQuantity = 99
      }
    },
  }
}
</script>

<style scoped>
/* ========== Modal 外觀 ========== */
.cart-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cart-modal {
  background: #fff;
  border-radius: 6px;
  padding: 10px;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
}

/* ========== 主要區域 ========== */
.detail-top-section {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.detail-media {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible !important;
}

.detail-media img,
.detail-media iframe,
.detail-media video {
  max-height: 400px;
  object-fit: cover;
}

.detail-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 價格顯示 */
.price {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.price del {
  color: #999;
  font-size: 1.2rem;
  text-decoration: line-through;
}

.discount-price {
  color: #e50000;
  font-size: 2.5rem;
  font-weight: bold;
}

/* 規格選擇 */
.variant-choices {
  text-align: left;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.variant-choices p {
  width: 100%;
  margin-bottom: 4px;
  font-weight: bold;
}

.variant-item {
  display: inline-block;
  padding: 6px 12px;
  border: 1px solid #ddd;
  cursor: pointer;
  border-radius: 4px;
}

.variant-item:hover {
  background: #f3f3f3;
}

.variant-item.is-active {
  border-color: #f90;
  background: #fff1e6;
}

/* 數量控制 */
.qty-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.quantity-control {
  display: inline-flex;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
  height: 40px;
}

.quantity-control button {
  width: 40px;
  background: #f5f5f5;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  color: #444;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quantity-control button:disabled {
  color: #999;
  cursor: not-allowed;
}

.quantity-control input {
  width: 60px;
  border: none;
  text-align: center;
  font-size: 1rem;
  outline: none;
  -moz-appearance: textfield;
  appearance: textfield;
}

.qty-hint {
  font-size: 0.85rem;
  color: #999;
}

/* 運送 / 付款方式 */
.shipping-info {
  text-align: left;
}

.shipping-info h4 {
  margin-bottom: 6px;
  font-size: 1rem;
  font-weight: bold;
}

.shipping-info ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.shipping-info li {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.shipping-info li img {
  width: 24px;
  height: 24px;
  object-fit: contain;
  margin-right: 8px;
}

/* 加入購物車 / 取消 */
.action-buttons {
  margin-top: auto;
  display: flex;
  justify-content: center;
  gap: 3px;
  padding: 10px;
}

.confirm-add-cart {
  background: #ff6600;
  color: #fff;
  border: none;
  padding: 10px 16px;
  cursor: pointer;
  border-radius: 4px;
  flex: 1;
}

.confirm-add-cart:hover {
  background: #ff5500;
}

.cancel-button {
  background: #ccc;
  color: #333;
  border: none;
  padding: 10px 16px;
  cursor: pointer;
  border-radius: 4px;
  flex: 1;
}

.cancel-button:hover {
  background: #bbb;
}

/* RWD */
@media (max-width: 768px) {
  .cart-modal {
    min-width: 90vw;
    max-width: 90vw;
    max-height: 80%;
    margin-top: 30px;
  }


  .detail-top-section {
    flex-direction: column;
    -webkit-overflow-scrolling: touch; /* iOS滾動慣性 */
    margin-top: 30px;
  }

  .detail-media,
  .detail-info {
    width: 100%;
  }

  .action-buttons {
    flex-direction: column;
  }

  .confirm-add-cart,
  .cancel-button {
    width: 100%;
    flex: none;
  }
}

/* 放大鏡 */
.magnifier-container {
  position: relative;
  display: inline-block;
  cursor: zoom-in;
}

.main-image {
  display: block;
  max-width: 100%;
}

.magnifier-lens {
  position: absolute;
  border: 2px solid #999;
  display: none;
  opacity: 0.3;
  pointer-events: none;
  z-index: 1002;
}

.magnifier-result {
  position: absolute;
  top: 0;
  left: calc(100% + 10px);
  width: 450px;
  height: 450px;
  border: 2px solid #999;
  border-radius: 4px;
  background-color: #fff;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  z-index: 1200;
  pointer-events: none;
}

/* 縮略圖列表 */
.thumbnail-list {
  margin-top: 10px;
  display: flex;
  gap: 5px;
  justify-content: center;
}

.thumbnail {
  width: 60px;
  height: 60px;
  border: 1px solid #ddd;
  cursor: pointer;
  overflow: hidden;
}

.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 主視區：固定大小，圖片/影片都填滿 */
.main-display {
  width: 100%;
  height: 450px;
  position: relative;
  overflow: visible;
}

.main-display img,
.main-display iframe,
.main-display video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.7);
  border: none;
  font-size: 2rem;
  padding: 0.5rem 1rem;
  cursor: pointer;
  z-index: 999;
}

.arrow-left {
  left: 0;
}

.arrow-right {
  right: 0;
}

.arrow:hover {
  background: rgba(255, 255, 255, 0.9);
}

/* YouTube iframe RWD */
.responsive-iframe {
  width: 100%;
  aspect-ratio: 16 / 9;
  /* 16:9 */
  border: 0;
}

/* 商品描述文字 */
.product-desc {
  font-size: 1.2rem;
  color: #777;
  margin-bottom: 8px;
  line-height: 1.4;
  text-align: left;
}


.error-text {
  width: 350px;
  background: #fff7c0;
  /* 淺黃色 */
  padding: 10px 15px;
  color: #e50000;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

/* Chrome、Safari、Edge */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
<style scoped>
/**
 * 商品庫存 / 已售出
 */
.stock-sold-wrapper {
  display: flex;
  gap: 20px;  
  margin: 12px 0;
  .info-box {
    display: flex;
    align-items: center;
    padding: 6px 12px;
    border-radius: 6px;
    background: linear-gradient(120deg, #fff 0%, #f3f3f3 100%);
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
    transition: box-shadow 0.3s ease, background 0.3s ease;
    cursor: default; 
    i {
      margin-right: 6px;
      font-size: 1.2rem;
      color: #444;
      transition: color 0.3s;
    }
    .number {
      font-size: 1.3rem; 
      font-weight: bold; 
      margin-right: 4px;
      color: #222;
    }
    .label {
      font-size: 0.85rem;
      color: #666;
    }
    &:hover {
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      background: linear-gradient(120deg, #fafafa 0%, #f0f0f0 100%);
      i {
        color: #ff6600; 
      }
    }
  }
  .sold-box {
    background: linear-gradient(120deg, #fff5f2 0%, #fce6e0 100%);
    &:hover {
      background: linear-gradient(120deg, #feece7 0%, #fbded6 100%);
    }
    i {
      color: #e74c3c; 
    }
  }
  .stock-box {
    background: linear-gradient(120deg, #f5fff2 0%, #e4fce0 100%);
    &:hover {
      background: linear-gradient(120deg, #ebffe6 0%, #d5fcd2 100%);
    }
    i {
      color: #27ae60; 
    }
  }
}
</style>